// InfrastructureManager.jsx — refactor to standard pagination pattern
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import Pagination from '../../components/Pagination'
import usePaginatedList from '../../hooks/usePaginatedList' // ✅ standard server-side pagination

/* ===================== Utilities ===================== */

const cx = (...classes) => classes.filter(Boolean).join(' ')

const getErrorMessage = (err, fallback = 'Unexpected error') => {
  if (!err) return fallback
  if (err.name === 'CanceledError' || err.name === 'AbortError') return 'Request was canceled'
  const respMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.response?.data?.error || err?.message
  return typeof respMsg === 'string' ? respMsg : fallback
}

const logHttpError = (scope, err, ctx = {}) => {
  const status = err?.response?.status
  const method = err?.config?.method?.toUpperCase?.()
  const url = err?.config?.url
  const params = err?.config?.params
  const data = err?.config?.data
  const detail = err?.response?.data ?? err?.toJSON?.() ?? { message: err?.message || String(err) }
  const ts = new Date().toISOString()
  // eslint-disable-next-line no-console
  console.groupCollapsed(`%c[${ts}] ${scope} – ${status || 'ERR'} ${method || ''} ${url || ''}`, 'color:#ef4444')
  // eslint-disable-next-line no-console
  console.log('Context:', ctx)
  // eslint-disable-next-line no-console
  console.log('Request params:', params)
  // eslint-disable-next-line no-console
  console.log('Request data:', data)
  // eslint-disable-next-line no-console
  console.log('Error detail:', detail)
  // eslint-disable-next-line no-console
  console.groupEnd()
}

/* ===================== Small UI Bits ===================== */

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cx(
      'px-4 py-2 rounded-xl text-sm font-semibold transition',
      active ? 'bg-indigo-600 text-white shadow' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/70'
    )}
  >
    {children}
  </button>
)

const Stat = ({ label, value }) => (
  <div className="flex flex-col px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700">
    <span className="text-xs text-slate-400">{label}</span>
    <span className="text-lg font-semibold text-slate-100">{value}</span>
  </div>
)

const SectionCard = ({ title, children, right }) => (
  <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 md:p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-base md:text-lg font-bold text-slate-100">{title}</h3>
      <div className="flex gap-2">{right}</div>
    </div>
    {children}
  </div>
)

/* ===================== Generic Modal ===================== */

const Modal = ({ open, onClose, title, children, footer }) => {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-2xl border border-slate-700 bg-slate-900 shadow-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
            <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
            <button onClick={onClose} className="rounded-lg px-2 py-1 text-slate-400 hover:text-slate-200 hover:bg-slate-800">✕</button>
          </div>
          <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">{children}</div>
          {footer && <div className="px-5 py-4 border-t border-slate-800 bg-slate-900/70 flex justify-end gap-2">{footer}</div>}
        </div>
      </div>
    </div>
  )
}

const TextInput = ({ label, hint, className = '', ...rest }) => (
  <label className="block space-y-1">
    <span className="text-sm text-slate-300">{label}</span>
    <input className={cx('w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 text-sm', className)} {...rest} />
    {hint && <span className="text-xs text-slate-400">{hint}</span>}
  </label>
)

const Select = ({ label, options, value, onChange, placeholder = 'Select…' }) => (
  <label className="block space-y-1">
    <span className="text-sm text-slate-300">{label}</span>
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 text-sm">
      <option value="">{placeholder}</option>
      {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  </label>
)

const TextArea = ({ label, ...rest }) => (
  <label className="block space-y-1">
    <span className="text-sm text-slate-300">{label}</span>
    <textarea className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 text-sm min-h-[120px]" {...rest} />
  </label>
)

const Button = ({ children, variant = 'default', className = '', ...rest }) => {
  const base = 'px-3 py-2 rounded-xl text-sm font-medium border'
  const styles = {
    default: 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700',
    ghost: 'bg-transparent border-transparent text-slate-300 hover:bg-slate-800/60',
    primary: 'bg-indigo-600 border-indigo-500 text-white hover:bg-indigo-500',
    danger: 'bg-transparent border-slate-700 text-red-300 hover:bg-red-900/30',
  }
  return <button className={cx(base, styles[variant], className)} {...rest}>{children}</button>
}

/* ===================== Helpers ===================== */

const toGB = (mb) => (typeof mb === 'number' ? Math.round(mb / 1024) : null)

const mapHypervisors = (hvItems) => (hvItems || []).map((it) => ({
  id: it.id,
  hostname: it.name ?? '—',
  vcpus: it.vcpus ?? null,
  vcpus_used: it.vcpus_used ?? null,
  mem_mb: it.memory_mb ?? null,
  mem_used: it.memory_mb_used ?? null,
  running_vms: it.running_vms ?? 0,
  state: it.state ?? 'unknown',
  status: it.status ?? 'unknown',
  availability_zone: it.availability_zone ?? null,
  aggregates: Array.isArray(it.aggregates) ? it.aggregates : [],
}))

const mapAggregates = (aggItems) => (aggItems || []).map((a) => ({
  id: a.id,
  name: a.name,
  availability_zone: a.availability_zone ?? '—',
  hosts: Array.isArray(a.hosts) ? a.hosts : [],
  metadata: a.metadata || {},
}))

const buildAZsFromHypervisors = (hypervisors) => {
  const azMap = new Map()
  ;(hypervisors || []).forEach((h) => {
    const az = h.availability_zone ?? '—'
    if (!azMap.has(az)) azMap.set(az, { name: az, available: true, hosts: [] })
    azMap.get(az).hosts.push(h.hostname)
  })
  return Array.from(azMap.values())
}

/* ===================== Modals ===================== */

const CreateAggregateModal = ({ open, onClose, onSubmit, computeTypes }) => {
  const [name, setName] = useState('')
  const [az, setAz] = useState('')
  const [computeCode, setComputeCode] = useState('') // kept for UX
  const [metadata, setMetadata] = useState('{}')
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!open) { setName(''); setAz(''); setComputeCode(''); setMetadata('{}'); setErr('') }
  }, [open])

  useEffect(() => { if (computeCode) setAz((prev) => (prev ? prev : computeCode)) }, [computeCode])

  const submit = async () => {
    try {
      setErr('')
      const parsed = metadata.trim() ? JSON.parse(metadata) : {}
      await onSubmit({ name, availability_zone: az || null, metadata: parsed })
    } catch {
      setErr('Metadata JSON is invalid.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Host Aggregate"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={submit}>Create</Button>
      </>}
    >
      <div className="space-y-4">
        {err && <div className="rounded-lg border border-red-800/50 bg-red-900/30 text-red-200 px-3 py-2 text-sm">{err}</div>}
        <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SSD only" />
        <Select label="Compute Type Code" value={computeCode} onChange={setComputeCode} options={(computeTypes || []).map((ct) => ({ value: ct.code, label: `${ct.code} — ${ct.name ?? ''}` }))} placeholder="Choose type…" />
        <TextInput label="Availability Zone" value={az} onChange={(e) => setAz(e.target.value)} />
        <TextArea label="Metadata JSON" value={metadata} onChange={(e) => setMetadata(e.target.value)} />
      </div>
    </Modal>
  )
}

const EditAggregateMetaModal = ({ open, onClose, onSubmit, aggregate }) => {
  const [payload, setPayload] = useState('{}')
  const [replace, setReplace] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (open && aggregate) { setPayload(JSON.stringify(aggregate.metadata || {}, null, 2)); setReplace(false); setErr('') }
  }, [open, aggregate])

  const submit = async () => {
    try {
      setErr('')
      const metadata = payload.trim() ? JSON.parse(payload) : {}
      await onSubmit({ metadata, replace })
    } catch {
      setErr('Metadata JSON is invalid.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit Metadata — ${aggregate?.name ?? ''}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={submit}>Save</Button>
      </>}
    >
      <div className="space-y-4">
        {err && <div className="rounded-lg border border-red-800/50 bg-red-900/30 text-red-200 px-3 py-2 text-sm">{err}</div>}
        <TextArea label="Metadata JSON" value={payload} onChange={(e) => setPayload(e.target.value)} />
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
          Replace all existing keys
        </label>
      </div>
    </Modal>
  )
}

const HostChangeModal = ({ open, onClose, onSubmit, aggregate, hypervisors, mode }) => {
  const [host, setHost] = useState('')
  useEffect(() => { if (open) setHost('') }, [open])

  const hvOptions = useMemo(() => {
    const names = (hypervisors || []).map((h) => h.hostname).filter(Boolean)
    return Array.from(new Set(names)).map((n) => ({ value: n, label: n }))
  }, [hypervisors])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${mode === 'assign' ? 'Assign' : 'Remove'} Host — ${aggregate?.name ?? ''}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant={mode === 'assign' ? 'primary' : 'danger'} onClick={() => host && onSubmit(host)}>
          {mode === 'assign' ? 'Assign' : 'Remove'}
        </Button>
      </>}
    >
      <div className="space-y-4">
        <Select label="Hostname" value={host} onChange={setHost} options={hvOptions} placeholder="Select host…" />
      </div>
    </Modal>
  )
}

/* ===================== Page ===================== */

export default function InfrastructureManager() {
  const clusterId = JSON.parse(
                    localStorage.getItem("selected_cluster")
                ).id;

  const [tab, setTab] = useState('hypervisors') // 'hypervisors' | 'aggregates' | 'az'
  const [search, setSearch] = useState('')

  const [computeTypes, setComputeTypes] = useState([])
  const [loadingCT, setLoadingCT] = useState(false)
  const [ctErr, setCtErr] = useState('')

  // API base paths
  const HV_LIST_URL = `/admin/os/hypervisors/cluster/${clusterId}`
  const AGG_LIST_URL = `/admin/os/hypervisors/cluster/${clusterId}/aggregates`
  const AGG_CREATE_URL = `/admin/os/hypervisors/cluster/${clusterId}/aggregates`
  const AGG_META_URL = (aggregateId) => `/admin/os/aggregates/cluster/${clusterId}/aggregates/${aggregateId}/metadata`
  const AGG_HOST_URL = (aggregateId, hostName) => `/admin/os/hypervisors/cluster/${clusterId}/aggregates/${aggregateId}/hosts/${encodeURIComponent(hostName)}`
  const AGG_DELETE_URL = (aggregateId) => `/admin/os/hypervisors/cluster/${clusterId}/aggregates/${aggregateId}`
  const CT_LIST_URL = `/admin/compute-types/cluster/${clusterId}`

  /* ---- usePaginatedList for both tables (standard) ---- */
  const hvFetcher = useCallback(async (params) => {
    const { data } = await axiosInstance.get(HV_LIST_URL, { params })
    return data // {items, meta} or Array
  }, [HV_LIST_URL])

  const aggFetcher = useCallback(async (params) => {
    const { data } = await axiosInstance.get(AGG_LIST_URL, { params })
    return data
  }, [AGG_LIST_URL])

  const {
    rows: hypervisors,
    meta: hvMeta,
    loading: loadingHV,
    error: hvErr,
    page: hvPage,
    pageSize: hvPageSize,
    onChange: onHVChange,
    refetch: refetchHV,
  } = usePaginatedList(hvFetcher, { initialPage: 1, initialPageSize: 25, deps: [clusterId] })

  const {
    rows: aggregates,
    meta: aggMeta,
    loading: loadingAGG,
    error: aggErr,
    page: aggPage,
    pageSize: aggPageSize,
    onChange: onAggChange,
    refetch: refetchAGG,
    reloadCurrentPageSafe: reloadAggPage,
  } = usePaginatedList(aggFetcher, { initialPage: 1, initialPageSize: 25, deps: [clusterId] })

  // Map rows after fetching
  const hvRows = useMemo(() => mapHypervisors(hypervisors), [hypervisors])
  const aggRows = useMemo(() => mapAggregates(aggregates), [aggregates])

  // Compute types (non-paginated list for selects)
  const fetchComputeTypes = useCallback(async () => {
    setLoadingCT(true)
    setCtErr('')
    try {
      const { data } = await axiosInstance.get(CT_LIST_URL, { params: { page: 1, page_size: 1000 } })
      const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
      setComputeTypes(items.map((ct) => ({ id: ct.id, code: ct.code, name: ct.name, description: ct.description })))
    } catch (e) {
      setCtErr(getErrorMessage(e, 'Failed to load compute types.'))
      setComputeTypes([])
      logHttpError('Fetch Compute Types', e, { url: CT_LIST_URL })
    } finally {
      setLoadingCT(false)
    }
  }, [CT_LIST_URL])

  // On cluster change, refetch lists (usePaginatedList handles resetting via deps)
  useEffect(() => { fetchComputeTypes() }, [fetchComputeTypes])

  /* ---- Aggregate actions ---- */
  const [createOpen, setCreateOpen] = useState(false)
  const [editMetaOpen, setEditMetaOpen] = useState(false)
  const [hostModal, setHostModal] = useState({ open: false, mode: 'assign', aggregate: null })
  const [editingAggregate, setEditingAggregate] = useState(null)

  const openCreate = () => setCreateOpen(true)
  const openEditMeta = (agg) => { setEditingAggregate(agg); setEditMetaOpen(true) }
  const openAssign = (agg) => setHostModal({ open: true, mode: 'assign', aggregate: agg })
  const openRemove = (agg) => setHostModal({ open: true, mode: 'remove', aggregate: agg })

  const handleCreateAggregate = async ({ name, availability_zone, metadata }) => {
    try {
      await axiosInstance.post(AGG_CREATE_URL, { name, availability_zone, metadata })
      setCreateOpen(false)
      await refetchAGG()
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to create aggregate.')
      logHttpError('Create Aggregate', e, { name, availability_zone })
      window.alert(msg)
    }
  }

  const handleEditAggregateMeta = async ({ metadata, replace }) => {
    if (!editingAggregate) return
    try {
      await axiosInstance.patch(AGG_META_URL(editingAggregate.id), { metadata, replace })
      setEditMetaOpen(false)
      setEditingAggregate(null)
      await refetchAGG()
    } catch (e) {
      const msg = e?.response?.data?.message || getErrorMessage(e, 'Failed to update metadata.')
      window.alert(msg)
      logHttpError('Update Aggregate Metadata', e, { aggregate_id: editingAggregate?.id, replace })
    }
  }

  const handleAssignHost = async (host) => {
    const agg = hostModal.aggregate
    if (!agg) return
    try {
      await axiosInstance.post(AGG_HOST_URL(agg.id, host))
      setHostModal({ open: false, mode: hostModal.mode, aggregate: null })
      await reloadAggPage?.()
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to assign host.')
      logHttpError('Assign Host', e, { aggregate_id: agg.id, host })
      window.alert(msg)
    }
  }

  const handleRemoveHost = async (host) => {
    const agg = hostModal.aggregate
    if (!agg) return
    try {
      await axiosInstance.delete(AGG_HOST_URL(agg.id, host))
      setHostModal({ open: false, mode: hostModal.mode, aggregate: null })
      await reloadAggPage?.()
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to remove host.')
      logHttpError('Remove Host', e, { aggregate_id: agg.id, host })
      window.alert(msg)
    }
  }

  const handleDeleteAggregate = async (agg) => {
    if (!window.confirm(`Delete aggregate "${agg.name}"?`)) return
    try {
      await axiosInstance.delete(AGG_DELETE_URL(agg.id))
      await reloadAggPage?.()
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to delete aggregate.')
      logHttpError('Delete Aggregate', e, { aggregate_id: agg.id })
      window.alert(msg)
    }
  }

  /* ---- Derived data ---- */
  const availabilityZones = useMemo(() => buildAZsFromHypervisors(hvRows), [hvRows])

  const filtered = useMemo(() => {
    if (!search.trim()) return { hypervisors: hvRows, aggregates: aggRows, availabilityZones, computeTypes }
    const q = search.toLowerCase()
    return {
      hypervisors: hvRows.filter((h) => (h.hostname || '').toLowerCase().includes(q)),
      aggregates: aggRows.filter((a) => (a.name || '').toLowerCase().includes(q) || (a.availability_zone || '').toLowerCase().includes(q)),
      availabilityZones: availabilityZones.filter((z) => (z.name || '').toLowerCase().includes(q)),
      computeTypes: computeTypes.filter((c) => (c.code || '').toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q)),
    }
  }, [search, hvRows, aggRows, availabilityZones, computeTypes])

  const totalRunningVMs = useMemo(() => hvRows.reduce((s, h) => s + (h.running_vms || 0), 0), [hvRows])

  const anyLoading = loadingHV || loadingAGG || loadingCT

  return (
    <div className="bg-gradient-to-br from-black via-gray-900 to-gray-800 text-gray-100 min-h-screen">
      <div className="max-w-full mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Link to="/admin/dashboard" className="hover:text-slate-200">Home</Link>
          <span>/</span>
          <span className="text-slate-200 font-semibold">Infrastructure</span>
        </div>

        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Infrastructure <span className="text-slate-400 text-base font-normal">(Cluster {clusterId})</span></h1>
          <div className="flex gap-2 md:ml-auto">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search hypervisor / aggregate / AZ / compute type…" className="px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 text-sm w-72" />
            <Button variant="ghost" onClick={() => setSearch('')}>Clear</Button>
            <Button onClick={() => { refetchHV(); refetchAGG(); fetchComputeTypes() }} disabled={anyLoading}>{anyLoading ? 'Loading…' : 'Refresh'}</Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          <TabButton active={tab === 'hypervisors'} onClick={() => setTab('hypervisors')}>Hypervisors</TabButton>
          <TabButton active={tab === 'aggregates'} onClick={() => setTab('aggregates')}>Host Aggregates</TabButton>
          <TabButton active={tab === 'az'} onClick={() => setTab('az')}>Availability Zones</TabButton>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Hypervisors" value={hvRows.length} />
          <Stat label="Aggregates" value={aggRows.length} />
          <Stat label="Availability Zones" value={availabilityZones.length} />
          <Stat label="Running VMs" value={totalRunningVMs} />
        </div>

        {/* Error banners */}
        {hvErr && (<div className="rounded-xl border border-red-800/50 bg-red-900/30 text-red-200 px-4 py-3">{String(hvErr)}</div>)}
        {aggErr && (<div className="rounded-xl border border-red-800/50 bg-red-900/30 text-red-200 px-4 py-3">{String(aggErr)}</div>)}
        {ctErr && (<div className="rounded-xl border border-red-800/50 bg-red-900/30 text-red-200 px-4 py-3">{String(ctErr)}</div>)}

        {/* Hypervisors */}
        {tab === 'hypervisors' && (
          <SectionCard title="Hypervisors" right={<Button onClick={refetchHV} disabled={loadingHV}>{loadingHV ? 'Loading…' : 'Refresh'}</Button>}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-slate-400">
                  <tr className="[&>th]:text-left [&>th]:py-2 [&>th]:px-3">
                    <th>Hostname</th>
                    <th>vCPU (used/total)</th>
                    <th>RAM (used/total)</th>
                    <th>Running VMs</th>
                    <th>AZ</th>
                    <th>State</th>
                    <th>Status</th>
                    <th className="text-right w-44">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {loadingHV && hvRows.length === 0 ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i} className="[&>td]:py-2 [&>td]:px-3 animate-pulse"><td colSpan={8}><div className="h-4 rounded bg-slate-700/50" /></td></tr>
                    ))
                  ) : filtered.hypervisors.length > 0 ? (
                    filtered.hypervisors.map((h) => {
                      const usedGB = toGB(h.mem_used)
                      const totalGB = toGB(h.mem_mb)
                      const vcpuUsed = h.vcpus_used ?? '—'
                      const vcpuTotal = h.vcpus ?? '—'
                      return (
                        <tr key={h.id} className="[&>td]:py-2 [&>td]:px-3">
                          <td className="font-medium text-slate-100">{h.hostname}</td>
                          <td>{vcpuUsed}/{vcpuTotal}</td>
                          <td>{usedGB !== null ? `${usedGB}GB` : '—'}/{totalGB !== null ? `${totalGB}GB` : '—'}</td>
                          <td>{h.running_vms ?? 0}</td>
                          <td>{h.availability_zone ?? '—'}</td>
                          <td className={h.state === 'up' ? 'text-green-400' : h.state === 'down' ? 'text-red-400' : 'text-slate-400'}>{h.state}</td>
                          <td className={h.status === 'enabled' ? 'text-green-400' : h.status === 'disabled' ? 'text-yellow-400' : 'text-slate-400'}>{h.status}</td>
                          <td className="text-right">
                            <div className="inline-flex gap-2">
                              <Button variant="ghost">Details</Button>
                              <Button variant="ghost">Toggle</Button>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr><td colSpan={8} className="py-6 text-center text-slate-400">No hypervisors found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {(hvRows.length > 0 || hvMeta?.total) && (
              <div className="mt-4"><Pagination meta={hvMeta} onChange={onHVChange} pageSizeOptions={[25, 40, 60, 100]} /></div>
            )}
          </SectionCard>
        )}

        {/* Aggregates */}
        {tab === 'aggregates' && (
          <SectionCard title="Host Aggregates" right={<><Button onClick={refetchAGG} disabled={loadingAGG}>{loadingAGG ? 'Loading…' : 'Refresh'}</Button><Button variant="primary" onClick={openCreate}>Create Aggregate</Button></>}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-slate-400">
                  <tr className="[&>th]:text-left [&>th]:py-2 [&>th]:px-3">
                    <th>Name</th>
                    <th>AZ</th>
                    <th>Hosts</th>
                    <th>Metadata</th>
                    <th className="text-right w-44">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {loadingAGG && aggRows.length === 0 ? (
                    [...Array(5)].map((_, i) => (<tr key={i} className="[&>td]:py-2 [&>td]:px-3 animate-pulse"><td colSpan={5}><div className="h-4 rounded bg-slate-700/50" /></td></tr>))
                  ) : filtered.aggregates.length > 0 ? (
                    filtered.aggregates.map((a) => (
                      <tr key={a.id} className="[&>td]:py-2 [&>td]:px-3">
                        <td className="font-medium text-slate-100">{a.name}</td>
                        <td>{a.availability_zone}</td>
                        <td>{a.hosts.join(', ') || '—'}</td>
                        <td className="text-slate-400">{Object.keys(a.metadata || {}).length === 0 ? '—' : Object.entries(a.metadata).map(([k, v]) => (<span key={k} className="mr-2 inline-block">{k}:{String(v)}</span>))}</td>
                        <td className="text-right">
                          <div className="inline-flex gap-2">
                            <Button variant="ghost" onClick={() => openEditMeta(a)}>Edit</Button>
                            <Button variant="ghost" onClick={() => openAssign(a)}>Assign Host</Button>
                            <Button variant="ghost" onClick={() => openRemove(a)}>Remove Host</Button>
                            <Button variant="danger" onClick={() => handleDeleteAggregate(a)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={5} className="py-6 text-center text-slate-400">No aggregates found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {(aggRows.length > 0 || aggMeta?.total) && (
              <div className="mt-4"><Pagination meta={aggMeta} onChange={onAggChange} pageSizeOptions={[25, 40, 60, 100]} /></div>
            )}
          </SectionCard>
        )}

        {/* Availability Zones */}
        {tab === 'az' && (
          <SectionCard title="Availability Zones" right={<Button onClick={refetchHV} disabled={loadingHV}>{loadingHV ? 'Loading…' : 'Refresh'}</Button>}>
            <div className="grid md:grid-cols-2 gap-4">
              {filtered.availabilityZones.map((z) => (
                <div key={z.name} className="rounded-xl border border-slate-700 p-4 bg-slate-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-slate-100 font-semibold">{z.name}</div>
                    <div className={cx('text-xs px-2 py-1 rounded-lg', z.available ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300')}>
                      {z.available ? 'available' : 'down'}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">Hosts</div>
                  <div className="text-sm text-slate-200">{z.hosts.join(', ') || '—'}</div>
                </div>
              ))}
              {!loadingHV && filtered.availabilityZones.length === 0 && (<div className="text-slate-400">No availability zones found.</div>)}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Modals */}
      <CreateAggregateModal open={createOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreateAggregate} computeTypes={computeTypes} />
      <EditAggregateMetaModal open={editMetaOpen} onClose={() => setEditMetaOpen(false)} onSubmit={handleEditAggregateMeta} aggregate={editingAggregate} />
      <HostChangeModal open={hostModal.open} onClose={() => setHostModal({ open: false, mode: hostModal.mode, aggregate: null })} onSubmit={hostModal.mode === 'assign' ? handleAssignHost : handleRemoveHost} aggregate={hostModal.aggregate} hypervisors={hvRows} mode={hostModal.mode} />
    </div>
  )
}
